// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  InventoryLotsResponse,
  inventoryLotsResponseSchema,
} from '@/inventory/api/types';
import { z } from 'zod';

export interface GetInventoryLotsOptions {
  itemKey: string;
  locationKey: string;
  includeEmptyLots?: boolean;
}

/**
 * Schema for the get inventory lots response.
 */
const getInventoryLotsResponseSchema = z.object({
  data: inventoryLotsResponseSchema,
  metadata: responseMetadataSchema,
});

/**
 * Gets lot details for a specific item at a specific location
 *
 * Endpoint: GET /v1/inventory/lots
 *
 * @param options - The item key, location key, and optional flag to include empty lots
 * @returns Promise resolving to inventory lots data
 */
export async function getInventoryLots(
  options: GetInventoryLotsOptions
): Promise<InventoryLotsResponse> {
  const { itemKey, locationKey, includeEmptyLots = false } = options;
  const url = '/v1/inventory/lots';
  const response = await api.get(url, {
    params: { itemKey, locationKey, includeEmptyLots },
  });
  const { data } = await parseResponse(
    getInventoryLotsResponseSchema,
    response.data,
    {
      schemaName: 'InventoryLots',
      endpoint: url,
    }
  );
  return data;
}
